'use client';

import { c as _c } from "react/compiler-runtime";
import { Children, type ComponentProps, type ReactElement } from 'react';
import { Avatar as AvatarPrimitive } from 'radix-ui';
import { cn } from './lib/cn';
type AvatarSize = '24' | '32' | '40' | '48' | '56' | '72';
interface AvatarProps extends ComponentProps<typeof AvatarPrimitive.Root> {
  badge?: boolean;
  badgeCount?: number;
  size?: AvatarSize;
}
const sizeClasses: Record<AvatarSize, string> = {
  '24': 'size-6',
  '32': 'size-8',
  '40': 'size-10',
  '48': 'size-12',
  '56': 'size-14',
  '72': 'size-[72px]'
};
const initialsFontClasses: Record<AvatarSize, string> = {
  '24': 'text-[10px] leading-3',
  '32': 'text-xs leading-[14px]',
  '40': 'text-xs leading-[14px]',
  '48': 'text-sm leading-5',
  '56': 'text-lg leading-6 tracking-[-0.5px]',
  '72': 'text-[22px] leading-7 tracking-[-1px]'
};
const badgeClasses: Record<AvatarSize, {
  position: string;
  size: string;
  showCount: boolean;
}> = {
  '24': {
    position: 'bottom-0 right-0',
    size: 'size-[6px] p-[2px]',
    showCount: false
  },
  '32': {
    position: 'bottom-0 right-0',
    size: 'size-[6px] p-[2px]',
    showCount: false
  },
  '40': {
    position: 'top-6 right-0',
    size: 'h-4 min-w-4 max-w-[34px] px-1',
    showCount: true
  },
  '48': {
    position: 'bottom-0 right-0',
    size: 'h-4 min-w-4 max-w-[34px] px-1',
    showCount: true
  },
  '56': {
    position: 'bottom-0 right-0',
    size: 'h-4 min-w-4 max-w-[34px] px-1',
    showCount: true
  },
  '72': {
    position: 'bottom-0 right-0',
    size: 'h-4 min-w-4 max-w-[34px] px-1',
    showCount: true
  }
};
function Avatar(t0) {
  const $ = _c(23);
  let badgeCount;
  let children;
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      size: t1,
      badge: t2,
      badgeCount,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = badgeCount;
    $[2] = children;
    $[3] = className;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
  } else {
    badgeCount = $[1];
    children = $[2];
    className = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const size = t1 === undefined ? "32" : t1;
  const badge = t2 === undefined ? false : t2;
  const badgeConfig = badgeClasses[size];
  const showBadgeCount = badgeConfig.showCount && badgeCount !== undefined;
  const t3 = sizeClasses[size];
  let t4;
  if ($[7] !== className || $[8] !== t3) {
    t4 = cn("relative flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-blue-100", t3, className);
    $[7] = className;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== children || $[11] !== props || $[12] !== t4) {
    t5 = <AvatarPrimitive.Root className={t4} data-slot="avatar" {...props}>{children}</AvatarPrimitive.Root>;
    $[10] = children;
    $[11] = props;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== badge || $[15] !== badgeConfig.position || $[16] !== badgeConfig.size || $[17] !== badgeCount || $[18] !== showBadgeCount) {
    t6 = badge && <div className={cn("absolute flex items-center justify-center rounded-full border-2 border-white", badgeConfig.position, badgeConfig.size, showBadgeCount ? "bg-red-100" : "bg-red-600")}>{showBadgeCount && <span className="font-medium text-[10px] text-red-900 leading-3">{badgeCount}</span>}</div>;
    $[14] = badge;
    $[15] = badgeConfig.position;
    $[16] = badgeConfig.size;
    $[17] = badgeCount;
    $[18] = showBadgeCount;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t5 || $[21] !== t6) {
    t7 = <div className="relative inline-block">{t5}{t6}</div>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}
function AvatarImage(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("aspect-square size-full object-cover", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <AvatarPrimitive.Image className={t1} data-slot="avatar-image" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
interface AvatarFallbackProps extends ComponentProps<typeof AvatarPrimitive.Fallback> {
  size?: AvatarSize;
  variant?: 'initials' | 'icon';
}
function AvatarFallback(t0) {
  const $ = _c(14);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      size: t1,
      variant: t2,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const size = t1 === undefined ? "32" : t1;
  const variant = t2 === undefined ? "initials" : t2;
  let t3;
  if ($[6] !== className || $[7] !== size || $[8] !== variant) {
    t3 = cn("flex size-full items-center justify-center rounded-full font-medium", variant === "initials" ? cn("bg-role-surface-container text-role-on-surface-container", initialsFontClasses[size]) : "bg-role-surface-container text-role-on-surface-container", className);
    $[6] = className;
    $[7] = size;
    $[8] = variant;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== children || $[11] !== props || $[12] !== t3) {
    t4 = <AvatarPrimitive.Fallback className={t3} data-slot="avatar-fallback" {...props}>{children}</AvatarPrimitive.Fallback>;
    $[10] = children;
    $[11] = props;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
interface AvatarGroupProps {
  children: [ReactElement, ReactElement];
  className?: string;
  layout?: 'diagonal' | 'horizontal';
  size?: AvatarSize;
}
const dualSizeClasses: Record<AvatarSize, string> = {
  '24': 'size-4',
  '32': 'size-5',
  '40': 'size-[26px]',
  '48': 'size-[30px]',
  '56': 'size-9',
  '72': 'size-12'
};
const dualOffsetClasses: Record<AvatarSize, string> = {
  '24': 'top-1',
  '32': 'top-[6px]',
  '40': 'top-[7px]',
  '48': 'top-[9px]',
  '56': 'top-[10px]',
  '72': 'top-1/2 -translate-y-1/2'
};
function AvatarGroup(t0) {
  const $ = _c(25);
  const {
    className,
    size: t1,
    layout: t2,
    children
  } = t0;
  const size = t1 === undefined ? "32" : t1;
  const layout = t2 === undefined ? "diagonal" : t2;
  let t3;
  if ($[0] !== children) {
    t3 = Children.toArray(children);
    $[0] = children;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [firstChild, secondChild] = t3;
  const t4 = sizeClasses[size];
  let t5;
  if ($[2] !== className || $[3] !== t4) {
    t5 = cn("relative", t4, className);
    $[2] = className;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = dualSizeClasses[size];
  let t7;
  if ($[5] !== layout || $[6] !== size || $[7] !== t6) {
    t7 = cn("absolute overflow-hidden rounded-full", t6, layout === "diagonal" ? "top-0 left-0" : cn("left-0", dualOffsetClasses[size]));
    $[5] = layout;
    $[6] = size;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== firstChild || $[10] !== t7) {
    t8 = <div className={t7}>{firstChild}</div>;
    $[9] = firstChild;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = dualSizeClasses[size];
  let t10;
  if ($[12] !== layout || $[13] !== size || $[14] !== t9) {
    t10 = cn("absolute rounded-full border-2 border-white", t9, layout === "diagonal" ? "right-0 bottom-0" : cn("right-0", dualOffsetClasses[size]));
    $[12] = layout;
    $[13] = size;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== secondChild) {
    t11 = <div className="size-full overflow-hidden rounded-full">{secondChild}</div>;
    $[16] = secondChild;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t10 || $[19] !== t11) {
    t12 = <div className={t10}>{t11}</div>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12 || $[22] !== t5 || $[23] !== t8) {
    t13 = <div className={t5} data-slot="avatar-group">{t8}{t12}</div>;
    $[21] = t12;
    $[22] = t5;
    $[23] = t8;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
}
export { Avatar, AvatarFallback, AvatarGroup, AvatarImage };